import { useEffect, useState } from "react";
import { BadgeCheck, BellRing, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  notificationPreferencesApi,
  type NotificationPreference,
} from "@/lib/notification-preferences.api";
import { LoadingLine } from "../components/LoadingLine";
import { SettingsKpi } from "../components/SettingsKpi";

export function NotificationsTab() {
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  useEffect(() => {
    notificationPreferencesApi.list().then((response) => {
      setPrefs(response.data);
      setLoading(false);
    });
  }, []);

  async function handleToggle(
    eventCode: string,
    field: "inAppEnabled" | "emailEnabled",
    value: boolean,
  ) {
    setPrefs((current) =>
      current.map((pref) => (pref.eventCode === eventCode ? { ...pref, [field]: value } : pref)),
    );
    setPendingKey(`${eventCode}:${field}`);
    try {
      await notificationPreferencesApi.update(eventCode, { [field]: value });
    } catch {
      setPrefs((current) =>
        current.map((pref) =>
          pref.eventCode === eventCode ? { ...pref, [field]: !value } : pref,
        ),
      );
    } finally {
      setPendingKey(null);
    }
  }

  if (loading) return <LoadingLine label="Chargement des préférences..." />;

  const emailCount = prefs.filter((pref) => pref.emailEnabled).length;
  const inAppCount = prefs.filter((pref) => pref.inAppEnabled).length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-3">
        <SettingsKpi icon={BellRing} label="Événements suivis" value={String(prefs.length)} />
        <SettingsKpi
          icon={BadgeCheck}
          label="Notifications dans l'application"
          value={String(inAppCount)}
        />
        <SettingsKpi
          icon={Mail}
          label="E-mails activés"
          value={String(emailCount)}
          tone={emailCount > 0 ? undefined : "warning"}
        />
      </div>

      <section className="border border-border bg-card">
        <div className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">Préférences par événement</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Contrôle, pour chaque événement système, s'il crée une notification dans l'application
            et/ou envoie un e-mail. Les e-mails de documents restent gérés dans l'onglet E-mails.
          </p>
        </div>
        <div className="divide-y divide-neutral-100">
          {prefs.map((pref) => (
            <div key={pref.eventCode} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{pref.label}</p>
                {pref.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{pref.description}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-muted-foreground">Application</span>
                  <Switch
                    checked={pref.inAppEnabled}
                    disabled={pendingKey === `${pref.eventCode}:inAppEnabled`}
                    onCheckedChange={(value) => handleToggle(pref.eventCode, "inAppEnabled", value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium text-muted-foreground">Email</span>
                  <Switch
                    checked={pref.emailEnabled}
                    disabled={pendingKey === `${pref.eventCode}:emailEnabled`}
                    onCheckedChange={(value) => handleToggle(pref.eventCode, "emailEnabled", value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
